import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
import { Quests } from './quests.js';
import { showAchievementBanner } from './banner.js';

/**
 * UI.ExchangeQuest — the Moon Exchange unlock, pinned into the quest rail.
 *
 * WHY IT IS PINNED RATHER THAN A REAL QUEST. The rolling quests are assigned,
 * expire and are replaced; this one is a one-time account milestone that ends
 * permanently the moment it completes. Putting it in the rotation would spend
 * one of three slots on something the player cannot decline and cannot repeat,
 * and would push a genuine quest out for as long as it took. So it is pinned
 * above the rotation and counted in BOTH halves of the rail's fraction, the
 * same way the invitation is: an extra row the player has, not a slot taken
 * from them. Owner's instruction: "shouldnt take slots".
 *
 * IT DISAPPEARS WHEN DONE. Once the account is unlocked there is nothing left
 * to track, so the row is not rendered at all — it never becomes a permanent
 * "completed" fixture cluttering a rail that has to fit five rows on a phone.
 */
let ExchangeQuest = {};

ExchangeQuest = (function () {
  const ITEM_ID = 'lobby-quest-exchange';
  let cached = null;
  let inFlight = null;

  /* `i18n.t` answers a key it cannot resolve WITH THE KEY — and until ui.json
     has loaded, that is every key. This helper used to default the result with
     `||`, so the fallback never fired: a fresh Chrome showed
     "collection.exchange.questText" in the rail. The fallback is interpolated
     too, or a cold load would print a literal `{{needed}}`. */
  function uiText(key, fallback, replacements = {}) {
    let translated = null;
    try { translated = i18n?.t?.(key, replacements); } catch (_) { /* i18n not ready */ }
    const source = typeof translated === 'string' && translated && translated !== key
      ? translated
      : fallback;
    return Object.entries(replacements).reduce(
      (text, [name, value]) => text.split(`{{${name}}}`).join(String(value)),
      String(source),
    );
  }

  /**
   * @param {string} tag
   * @param {{ className?: string, id?: string, text?: string }} [options]
   * @returns {HTMLElement}
   */
  const make = (tag, options = {}) => {
    const { className, id, text } = options;
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (id) el.id = id;
    if (text !== undefined) el.textContent = text;
    return el;
  };

  /** Only an account that is still short of the bar has anything to track. */
  function isTrackable(state) {
    if (!state) return false;
    if (state.unlocked !== false) return false;
    return Number(state.required) > 0;
  }

  /** The row's words — everything in it that a language can change. */
  function words(state) {
    const needed = Number(state.required) || 0;
    return {
      text: uiText(
        'collection.exchange.questText',
        'Moon Exchange — win {{needed}} matches',
        { needed },
      ),
      reward: uiText('collection.exchange.questReward', 'Unlocks trading'),
    };
  }

  function build(state) {
    const needed = Number(state.required) || 0;
    const done = Math.min(Math.max(0, Number(state.finished) || 0), needed);
    const copy = words(state);

    const item = make('div', { id: ITEM_ID });
    /* `lobby-quest-item` so it inherits the rail's layout exactly; the modifier
       only changes the accent, never the geometry. */
    item.className = 'lobby-quest-item lobby-quest-item--exchange';
    if (needed > 0 && done >= needed) item.classList.add('lobby-quest-item--done');

    const text = make('div', { className: 'lobby-quest-text', text: copy.text });

    const progress = make('div', { className: 'lobby-quest-progress', text: `${done}/${needed}` });

    const bar = make('div', { className: 'lobby-quest-bar' });
    const fill = make('div', { className: 'lobby-quest-bar-fill' });
    fill.style.width = `${Math.max(0, Math.min(100, needed ? (done / needed) * 100 : 0))}%`;
    bar.appendChild(fill);

    const reward = make('div', { className: 'lobby-quest-exchange-reward', text: copy.reward });

    item.append(text, progress, bar, reward);
    return item;
  }

  function setText(el, value) {
    if (!el || el.textContent === value) return false;
    el.textContent = value;
    return true;
  }

  /**
   * Rewrite the row's words in the language that has since arrived.
   *
   * The row is usually built BEFORE ui.json lands — the rail's reads race the
   * translation fetch on every cold load, and win it on a fresh browser — so it
   * carries the English fallback until something writes it again. Only the
   * words are written, and only where they differ: rebuilding would re-insert
   * the row under the player, and an English reader needs nothing at all.
   */
  function relocalize(list) {
    const target = list || document.getElementById('lobby-quests-list');
    const row = target?.querySelector?.(`#${ITEM_ID}`);
    if (!row || !isTrackable(cached)) return false;
    const copy = words(cached);
    const text = setText(row.querySelector('.lobby-quest-text'), copy.text);
    const reward = setText(row.querySelector('.lobby-quest-exchange-reward'), copy.reward);
    return text || reward;
  }

  /** How many rail rows this occupies — counted in both halves of the count. */
  function count() {
    return isTrackable(cached) ? 1 : 0;
  }

  /**
   * Put the pinned row into `list`, or take it away.
   *
   * BELOW the invitation when both are present. The invitation is time-boxed
   * and can expire; this one cannot, so the thing with a deadline reads first.
   */
  function mount(list, { flash = false } = {}) {
    const target = list || document.getElementById('lobby-quests-list');
    if (!target) return false;
    target.querySelector(`#${ITEM_ID}`)?.remove();
    if (!isTrackable(cached)) return false;

    const item = build(cached);
    // Spent once played, or it replays whenever the rail is shown again.
    if (flash) Quests?.armTick?.(item);
    const invite = target.querySelector('#lobby-quest-invite');
    if (invite) invite.after(item);
    else target.prepend(item);
    return true;
  }

  /**
   * THE MOMENT IT OPENS.
   *
   * Fired only on an observed TRANSITION — we had read `locked` and now read
   * `unlocked` — so a returning player who was already past the bar never sees
   * it. A first read of `unlocked` is not a transition: `cached` is null then,
   * and null is not false.
   *
   * The banner is the celebration; the message that follows tells the player
   * what to DO with it, because an unlock nobody acts on may as well not have
   * happened. Both are decoration over an authoritative fact, so a failure in
   * either must never look like a failure to unlock.
   */
  function celebrate() {
    try {
      showAchievementBanner(
        uiText('collection.exchange.unlockedTitle', 'The Moon Exchange is open'),
        uiText('collection.exchange.unlockedBody',
          'You can buy and sell cards with other players now.'),
        null,
        'exchange',
        { durationMs: 5200 },
      );
    } catch (_) { /* the unlock stands whether or not it is announced */ }

    try { showUnlockedMessage(); } catch (_) { /* ditto */ }

    /* Announced for anything else that wants to react — and for tests, which
       should not have to scrape an overlay to know this fired. */
    try {
      window.dispatchEvent(new CustomEvent('moonfall:exchange-unlocked', {
        detail: { required: cached?.required ?? null },
      }));
    } catch (_) { /* ditto */ }
  }

  /**
   * "It is open — go and look."
   *
   * Reuses the starter-reward overlay classes deliberately: that look is the
   * approved one for a moment like this, and a second bespoke celebration
   * style is exactly how these screens drift apart. The CTA takes the player
   * straight there, because a message telling someone about a feature without
   * a door to it is an advert.
   */
  function showUnlockedMessage() {
    const ID = 'exchange-unlocked-overlay';
    if (document.getElementById(ID)) return;

    const overlay = make('div', { id: ID, className: 'starter-reward-overlay' });
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', `${ID}-title`);

    const card = make('section', { className: 'starter-reward-card' });
    card.append(
      make('p', {
        className: 'starter-reward-eyebrow',
        text: uiText('collection.exchange.questRewardReady', 'Unlocked'),
      }),
      make('h2', {
        id: `${ID}-title`,
        className: 'starter-reward-title',
        text: uiText('collection.exchange.unlockedTitle', 'The Moon Exchange is open'),
      }),
      make('p', {
        className: 'starter-reward-subline',
        text: uiText('collection.exchange.unlockedBody',
          'You can buy and sell cards with other players now.'),
      }),
    );

    /* Built directly rather than through `make`, because a button needs its
       own type and `make` hands back a plain HTMLElement. */
    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'starter-reward-cta';
    cta.textContent = uiText('collection.exchange.unlockedCta', 'Open the Exchange');

    const close = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey, true);
    };
    function onKey(event) {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
    }

    cta.addEventListener('click', () => {
      close();
      /* Through the product's own controls, not by poking at internals: open
         the Collection, then its Exchange tab. If either is absent the message
         simply closes rather than throwing on a celebration. */
      try {
        document.getElementById('open-collection-btn')?.click();
        window.setTimeout(() => {
          document.getElementById('collection-tab-exchange')?.click();
        }, 0);
      } catch (_) { /* the unlock stands regardless */ }
    });

    card.appendChild(cta);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    document.addEventListener('keydown', onKey, true);
    cta.focus();
  }

  /** Re-read the gate and re-render. Deduplicated like the invitation's;
      `deferMount` leaves the render to the quest rail's single pass. */
  function refresh({ flash = false, deferMount = false } = {}) {
    if (inFlight) return inFlight;
    const read = MoonfallCloud?.moonExchange?.getConfig;
    if (typeof read !== 'function') return Promise.resolve(false);

    inFlight = Promise.resolve()
      .then(() => read.call(MoonfallCloud.moonExchange))
      .then((config) => {
        const wasLocked = cached?.unlocked === false;
        cached = typeof config?.sellUnlocked === 'boolean'
          ? {
            unlocked: config.sellUnlocked,
            finished: config.sellUnlockFinished,
            required: config.sellUnlockRequired,
          }
          : null;
        const opened = wasLocked && cached?.unlocked === true;
        if (deferMount) {
          if (opened) celebrate();
          return isTrackable(cached);
        }
        const shown = mount(null, { flash });
        try { Quests?.syncSlotCount?.(); } catch (_) { /* rail absent */ }
        if (opened) celebrate();
        return shown;
      })
      .catch(() => false)
      .finally(() => { inFlight = null; });
    return inFlight;
  }

  return {
    mount, refresh, count, relocalize, __setState: (state) => { cached = state; },
  };
}());

export { ExchangeQuest };
